import { useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Loader2, XCircle } from 'lucide-react'
import { ordersService } from '@/services/orders'
import { useCartStore } from '@/stores/cartStore'

/**
 * PayPal sends the buyer here after they approve the payment. We capture it via
 * /orders/payments/confirm (using the payment reference), then go to the order confirmation.
 */
export default function PaypalReturn() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const clearCart = useCartStore((s) => s.clearCart)
  const [error, setError] = useState<string | null>(null)
  const ran = useRef(false)

  const ref = params.get('ref')
  const orderId = params.get('order_id')

  useEffect(() => {
    if (ran.current) return
    ran.current = true
    ;(async () => {
      if (!ref) {
        setError('Missing payment reference. If you were charged, please contact support.')
        return
      }
      try {
        await ordersService.confirmPayment(ref)
        clearCart()
        navigate(`/order-confirmation/${orderId || ''}`)
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'We could not confirm your PayPal payment. Please contact support.')
      }
    })()
  }, [ref, orderId, navigate, clearCart])

  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center">
      {!error ? (
        <>
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-primary-600" />
          <p className="text-gray-600">Confirming your PayPal payment…</p>
        </>
      ) : (
        <>
          <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment not confirmed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/account/orders')}
            className="px-5 py-2.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700"
          >
            View my orders
          </button>
        </>
      )}
    </div>
  )
}
